import React from 'react'
import WorkLayout from '../../../layouts/Work'
import SlideDeck from '../../../assets/decks/wm_case-study_master.pdf'
import displayImg from '../../../assets/images/walmart_display.png'
import mobileImg from '../../../assets/images/walmart_mobile-2.png'
import desktopImg from '../../../assets/images/walmart_desktop.png'

const NAV_SECTIONS = [
  { id: 'overview', label: 'Overview' },
  { id: 'highlight', label: 'Highlight' },
  { id: 'summary', label: 'Summary' },
  { id: 'research', label: 'Research' },
  { id: 'scope', label: 'Native app impacts' },
  { id: 'mobile', label: 'Mobile' },
  { id: 'design-system', label: 'Design system' },
  { id: 'testing', label: 'Testing' },
  { id: 'desktop', label: 'Desktop' }
]

const Work = () => {
  return (
    <WorkLayout
      pageTitle="Out of stock recapture"
      date="2021-2022"
      role="Contractor"
      company="Walmart"
      slidesLink={SlideDeck}
      sections={NAV_SECTIONS}
    >
      <section id="overview">
        <img src={displayImg} alt="walmart beauty shot"/>
      </section>

      <section id="highlight" className='work-highlight'>
        <h2>A solution that allows Walmart International customers to get notified when products are back in stock.</h2>
      </section>

      <section id="summary" className='work-summary'>
        <header className="work-header">
          <h2>Summary</h2>
        </header>
        <p>
          Walmart’s Canada and Mexico mobile app and website lacked an effective backorder system, which often results in users bouncing to a competitor's website to find out-of-stock items.
        </p>
        <p>
          Aiming to improve the user experience and recapture the customer after items gained stock, we implemented a backorder system that allows users to sign up for notifications when items are back in stock, and provides a seamless experience when adding the item to their cart.
        </p>
        <section className='work-summary__section'>
          <h3>My role</h3>
          <p>I worked as the Lead Product Designer alongside the Product Manager, UX Design System and Accessibility teams</p>
        </section>
      </section>

      <section id="research" className="work-feature">
        <h2>Research</h2>
        <ul>
          <li>
            <header className="work-header">
              <h4>Insight #1</h4>
              <h3>Target Users</h3>
            </header>
            <p>
              Users were most likely to need the backorder feature for products that have variations such as color or flavor. Furniture is the largest effected category.
            </p>
          </li>

          <li>
            <header className="work-header">
              <h4>Insight #2</h4>
              <h3>No brand loyalty</h3>
            </header>
            <p>
              When users encountered an out-of-stock item on Walmart's mobile app, they were likely to immediately search for the item on a competitor's app or website.
            </p>
          </li>

          <li>
            <header className="work-header">
              <h4>Insight #3</h4>
              <h3>Availability > price</h3>
            </header>
            <p>
              Users were more likely to purchase from a competitor if the item was in stock and available for immediate delivery, regardless of the price.
            </p>
          </li>
        </ul>
      </section>

      <section id="scope">
        <header className="work-header">
          <h4>Expanding the Scope</h4>
          <h2>Native app impacts</h2>
        </header>
        <p>
          While mapping the customer experience, I discovered deep-links that directly open into the app if installed. This introduced a new dependency — we needed to bring in the mobile app team to ensure a seamless handoff and consistent experience across platforms. This had another set of complexities for timing and component creation.
        </p>
      </section>

      <section id="mobile">
        <img src={mobileImg} alt="walmart mobile solution"/>
      </section>

      <section id="design-system">
        <header className="work-header">
          <h4>International vs. U.S. Design System</h4>
          <h2>Which design system to follow?</h2>
        </header>
        <p>At the time, we had three viable paths: Walmart International, current Walmart US, or early adoption of Walmart Glass, which was in phased rollout across both domains. Using Walmart International DS would solve the short-term problem but introduce future rework. Adopting Walmart Glass aligned with the long-term design direction, though it required accepting temporary inconsistencies.</p>
        <p>After raising these considerations with the UX Design teams, we made a deliberate decision to move forward with Walmart Glass and bring the required components into the design system.</p>
      </section>

      <section id="testing">
        <header className="work-header">
          <h2>Testing</h2>
        </header>
        <h3>Missing entry point</h3>
        <p>We observed when users searched for an item with variants that the CTA changes to “Choose Options” that initiates a modal. Users were more likely to experience the out-of-stock notification here than the main product page.</p>
        <h3>Streamlined flow for logged-in users</h3>
        <p>Our research showed users were more likely to use the backorder feature when the process felt fast and effortless. By prepopulating their email address, we reduced friction and minimized input errors, making it easier for users to get notified about restocks. This small change significantly improved both usability and reliability of the feature.</p>
        <h3>Alert confusion</h3>
        <p>User testing revealed that many users skipped entering their email and clicked the “Alert” CTA right away. We believe this happened because the button closely resembled the “Add to Cart” CTA in both design and placement, creating a familiar pattern. Plus, the button was enabled by default, reinforcing the assumption that no additional input was needed.</p>
      </section>

      <section id="desktop">
        <img src={desktopImg} alt="walmart desktop solution"/>
      </section>
    </WorkLayout>
  )
}

export default Work;
